import AuthService from '@/services/auth'
import { errorMessage } from '@/utils/errorMessage'

// getActiveSession rejects with either the server's response (it answered) or the raw
// transport error (it never did), and only the first kind says anything about whether the
// session is still valid: 401/403 means it is gone, while an unreachable backend means only
// that we could not ask. Callers use this to tell "offer re-authentication" apart from
// "ride out the blip".
export function isAuthRejection (rejection) {
    const status = rejection && rejection.status
    return status === 401 || status === 403
}

export default {
    namespaced: true,
    state: {
        session: {},
        isLoggedIn: false,
    },
    getters: {
        // The session payload is the flat req.user object the API's /session endpoint
        // returns: { user_id, username, email, scope, permissions } — there is no nested
        // user. Presence is judged by email, the same field getActiveSession itself
        // treats as the mark of a valid session.
        session: state => state.session,
        user: state => state.session,
        permissions: state => state.session.permissions,
        isLoggedIn: state => !!state.session.email,
    },
    mutations: {
        setSession (state, val) {
            state.session = val || {}
        },
    },
    actions: {
        getActiveSession ({ commit }) {
            return new Promise((resolve, reject) => {
                AuthService.getSession()
                .then(res => {
                    if (!res?.data?.email) {
                        // A 200 with no user is the server stating there is no session —
                        // as final as a 401, so it rejects in the shape isAuthRejection
                        // recognizes.
                        return reject(Object.assign(new Error('Invalid session'), { status: 401 }))
                    }
                    commit('setSession', res.data)
                    return resolve(res.data)
                })
                .catch(err => {
                    // The message only: an axios error carries the whole request and
                    // response, including the Authorization header and body.
                    console.log(errorMessage(err))
                    // The held session survives every kind of failure. On a network blip
                    // the cookie is very likely still valid, and dropping local state
                    // would strip the permission-derived UI out from under the user
                    // mid-view. On an auth rejection the server already refuses every
                    // request, so stale local state authorizes nothing — while clearing
                    // it could unmount the very form holding the user's unsaved work.
                    // Deliberate sign-out is the one path that clears (setSession({})).
                    return reject(err.response || err)
                })
            })
        }
    }
}
